/**
 * Yon panel — jadvaldagi yozuvning to'liq kartochkasi.
 *
 * Sahifa almashmaydi: ro'yxat joyida qoladi, kartochka o'ngdan ochiladi.
 * `Esc` va fon bosilishi panelni yopadi, fokus panel ichiga ko'chiriladi.
 */
import { useEffect, useRef } from "react";
import type { ReactNode } from "react";

import { useI18n } from "@/shared/i18n";
import { Icon } from "@/shared/ui/Icon";

export function Drawer({
  open,
  title,
  subtitle,
  onClose,
  footer,
  wide = false,
  children,
}: {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  footer?: ReactNode;
  /** Keng panel: ekranning uchdan ikki qismi (murakkab formalar uchun). */
  wide?: boolean;
  children: ReactNode;
}) {
  const { t } = useI18n();
  const panel = useRef<HTMLDivElement>(null);

  /* `onClose` har renderda yangi funksiya bo'lishi mumkin: effektni unga bog'lamaymiz, aks holda
     har harf yozilganda panelga fokus qaytib, kiritish uziladi. */
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeRef.current();
    };
    document.addEventListener("keydown", onKey);
    panel.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  return (
    <>
      <button type="button" className="drawer-scrim" aria-label={t("action.close")} onClick={onClose} />
      <aside
        className={wide ? "drawer drawer-wide" : "drawer"}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panel}
      >
        <header className="drawer-head">
          <div>
            <h2>{title}</h2>
            {subtitle ? <p className="muted">{subtitle}</p> : null}
          </div>
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t("action.close")}>
            <Icon name="close" size={18} />
          </button>
        </header>
        <div className="drawer-body">{children}</div>
        {footer ? <footer className="drawer-foot">{footer}</footer> : null}
      </aside>
    </>
  );
}

/** Kartochka ichidagi «maydon → qiymat» ro'yxati. */
export function KeyValues({
  rows,
}: {
  rows: Array<{ label: string; value: ReactNode } | null | false>;
}) {
  const visible = rows.filter(Boolean) as Array<{ label: string; value: ReactNode }>;

  return (
    <dl className="kv">
      {visible.map((row) => (
        <div className="kv-row" key={row.label}>
          <dt>{row.label}</dt>
          <dd>{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Kartochka ichidagi bo'lim sarlavhasi. */
export function DrawerSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="drawer-section">
      <h3>{title}</h3>
      {children}
    </section>
  );
}
